import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, Lock, Eye, EyeOff, Compass, CheckCircle2, AlertTriangle, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useLang, LanguageToggle } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const RED = "#B0271C";

const PwField = ({ testId, label, value, onChange, onEnter }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="space-y-1.5">
      <Label className="text-[#4A3B2E] font-semibold">{label}</Label>
      <div className="relative">
        <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#B9A78C]" />
        <Input data-testid={testId} type={show ? "text" : "password"} value={value}
          onChange={(e) => onChange(e.target.value)} placeholder="Minimaal 8 tekens"
          className="pl-10 pr-10 h-12 bg-[#FBF7F0] border-[#EADFCB] rounded-xl"
          onKeyDown={(e) => e.key === "Enter" && onEnter()} />
        <button type="button" onClick={() => setShow((v) => !v)}
          aria-label={show ? "Wachtwoord verbergen" : "Wachtwoord tonen"}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-[#B9A78C] hover:text-[#5B4636] transition-colors">
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
};

export default function ResetPassword() {
  const { t } = useLang();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [status, setStatus] = useState("checking"); // checking | invalid | form | done
  const [busy, setBusy] = useState(false);
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");

  useEffect(() => {
    if (!token) { setStatus("invalid"); return; }
    api.post("/auth/reset-password/valideer", { token })
      .then((r) => setStatus(r.data.geldig ? "form" : "invalid"))
      .catch(() => setStatus("invalid"));
  }, [token]);

  const submit = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/auth/reset-password", {
        token, new_password: pw1, new_password_herhaal: pw2,
      });
      setStatus("done");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Er ging iets mis. Probeer het opnieuw.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FBF3E8] flex flex-col" data-testid="reset-password-page">
      <header className="w-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 h-20 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3">
            <span className="w-11 h-11 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: RED }}>
              <Compass className="w-6 h-6 text-white" />
            </span>
            <span className="font-heading font-extrabold text-xl text-[#2B211A]">Zoetwaren Kompas</span>
          </a>
          <LanguageToggle />
        </div>
      </header>

      <main className="flex-1 flex items-start justify-center px-4 pt-10 sm:pt-16 pb-16">
        <div className="w-full max-w-md bg-white rounded-[1.75rem] shadow-[0_20px_60px_-20px_rgba(74,49,34,0.35)] border border-[#F0E6D6] p-6 sm:p-9">

          {status === "checking" && (
            <div className="flex items-center gap-3 text-[#8A7660] py-6" data-testid="reset-checking">
              <Loader2 className="w-5 h-5 animate-spin" /> {t("reset.checking")}
            </div>
          )}

          {status === "invalid" && (
            <div data-testid="reset-invalid-message">
              <div className="w-12 h-12 rounded-full bg-[#FDECEA] flex items-center justify-center mb-4">
                <AlertTriangle className="w-6 h-6" style={{ color: RED }} />
              </div>
              <h2 className="font-heading text-2xl font-extrabold text-[#2B211A]">{t("reset.invalid")}</h2>
              <p className="text-[#8A7660] text-sm mt-2">{t("reset.invalid.hint")}</p>
              <Button data-testid="reset-to-login" onClick={() => { window.location.href = "/"; }}
                className="w-full mt-6 h-12 rounded-xl text-white font-semibold hover:brightness-95"
                style={{ backgroundColor: RED }}>
                {t("reset.tologin")} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}

          {status === "form" && (
            <>
              <h2 className="font-heading text-3xl font-extrabold text-[#2B211A]">{t("reset.title")}</h2>
              <p className="text-[#8A7660] text-sm mt-1 mb-6">{t("reset.subtitle")}</p>
              <div className="space-y-4">
                <PwField testId="reset-new-password" label={t("reset.new")} value={pw1} onChange={setPw1} onEnter={submit} />
                <PwField testId="reset-repeat-password" label={t("reset.repeat")} value={pw2} onChange={setPw2} onEnter={submit} />
              </div>
              <Button data-testid="reset-submit" onClick={submit} disabled={busy}
                className="w-full mt-6 h-12 rounded-xl text-white font-semibold text-base hover:brightness-95 transition-all"
                style={{ backgroundColor: RED }}>
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}
                {t("reset.submit")}
              </Button>
            </>
          )}

          {status === "done" && (
            <div data-testid="reset-success-message">
              <div className="w-12 h-12 rounded-full bg-[#EAF6EC] flex items-center justify-center mb-4">
                <CheckCircle2 className="w-6 h-6 text-[#2E7D32]" />
              </div>
              <h2 className="font-heading text-2xl font-extrabold text-[#2B211A]">{t("reset.success")}</h2>
              <p className="text-[#8A7660] text-sm mt-2">{t("reset.success.hint")}</p>
              <Button data-testid="reset-to-login" onClick={() => { window.location.href = "/"; }}
                className="w-full mt-6 h-12 rounded-xl text-white font-semibold hover:brightness-95"
                style={{ backgroundColor: RED }}>
                {t("reset.tologin")} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
